import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Loader2, Eye, EyeOff, Shield, ArrowRight } from 'lucide-react';
import { LOGO_FULL_FALLBACK } from './constants';

export function LoginPage() {
  const { login } = useAuth();
  const { logoUrl } = useSiteConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const afterLogin = location.state?.from?.pathname || '/dashboard';
  const LOGO_FULL = logoUrl || LOGO_FULL_FALLBACK;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate(afterLogin);
    } else if (result.requires_2fa) {
      setNeeds2FA(true);
      setTempToken(result.temp_token);
    } else {
      setError(result.error);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, tempToken, { totpCode });
    setLoading(false);

    if (result.success) {
      navigate(afterLogin);
    } else {
      setError(result.error || 'Invalid 2FA code');
    }
  };

  if (needs2FA) {
    return (
      <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Shield className="w-12 h-12 text-[#00E5FF] mx-auto mb-4" />
            <h1 className="font-outfit text-3xl font-bold text-white mb-2">Two-Factor Authentication</h1>
            <p className="text-[#A0B4CC]">Enter the code from your authenticator app</p>
          </div>
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-6">
              <form onSubmit={handle2FASubmit} className="space-y-4">
                {error && (
                  <div className="p-3 rounded-md bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] text-sm">{error}</div>
                )}
                <div className="space-y-2">
                  <Label className="text-white">Verification Code</Label>
                  <Input type="text" maxLength={6} value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000" autoFocus
                    className="bg-[#000B18] border-white/20 text-white text-center text-3xl tracking-[0.5em] font-mono h-14"
                    data-testid="2fa-login-code" />
                </div>
                <Button type="submit" disabled={loading || totpCode.length !== 6} className="w-full btn-primary h-11"
                  data-testid="2fa-login-submit">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
                <button type="button" onClick={() => { setNeeds2FA(false); setTotpCode(''); setError(''); }}
                  className="w-full text-center text-[#5C7599] text-sm hover:text-[#A0B4CC]">Back to login</button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_FULL} alt="All Global Access" className="h-16 mx-auto mb-6" />
          </Link>
          <h1 className="font-outfit text-3xl font-bold text-white mb-2">Welcome Back</h1>
          <p className="text-[#A0B4CC]">Sign in to access your dashboard</p>
        </div>

        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF] pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C7599] hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Link to="/forgot-password" className="text-sm text-[#00E5FF] hover:underline">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-11"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>Sign In <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-[#A0B4CC] text-sm">
                Don&apos;t have an account?{' '}
                <Link to="/register" className="text-[#00E5FF] hover:underline">
                  Create one
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

